import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    OrganizationGroup,
    OrganizationHierarchyLevel,
} from 'src/domain/organization';
import { UserRole } from 'src/domain/user';

export type OrganizationGroupFilterLevel = OrganizationHierarchyLevel & {
    groups: OrganizationGroup[];
    options: {
        id: string;
        label: string;
    }[];
    selectedIds: string[];
};

type UseOrganizationGroupFiltersOptions = {
    activeRoleGroupIds: string[];
    groups: OrganizationGroup[];
    hierarchy: OrganizationHierarchyLevel[];
    organizationAdmin?: boolean;
    role?: UserRole;
};

export const useOrganizationGroupFilters = ({
    activeRoleGroupIds,
    groups,
    hierarchy,
    organizationAdmin = false,
    role,
}: UseOrganizationGroupFiltersOptions) => {
    const [selectedGroupIdsByLevel, setSelectedGroupIdsByLevel] = useState<
        Record<string, string[]>
    >({});

    const childrenByParentId = useMemo(() => {
        const map = new Map<string, OrganizationGroup[]>();
        groups.forEach(group => {
            if (!group.parentId) {
                return;
            }

            const current = map.get(group.parentId) ?? [];
            current.push(group);
            map.set(group.parentId, current);
        });
        return map;
    }, [groups]);

    const rootGroupIds = useMemo(
        () =>
            new Set(
                groups.filter(group => !group.parentId).map(group => group.id),
            ),
        [groups],
    );

    const isOrganizationAdmin =
        organizationAdmin ||
        role === UserRole.organization ||
        role === UserRole.organizationB2C;
    const creatorHasTopLevelAccess =
        role === UserRole.creator &&
        (!activeRoleGroupIds.length ||
            activeRoleGroupIds.some(groupId => rootGroupIds.has(groupId)));
    const canUseFilters = isOrganizationAdmin || creatorHasTopLevelAccess;

    const filterableGroups = useMemo(() => {
        if (role !== UserRole.creator || !activeRoleGroupIds.length) {
            return groups;
        }

        const includedGroupIds = new Set<string>();
        const stack = [...activeRoleGroupIds];

        while (stack.length) {
            const current = stack.pop();
            if (!current || includedGroupIds.has(current)) {
                continue;
            }

            includedGroupIds.add(current);
            const children = childrenByParentId.get(current) ?? [];
            children.forEach(child => stack.push(child.id));
        }

        for (const groupId of activeRoleGroupIds) {
            let currentParentId = groups.find(
                group => group.id === groupId,
            )?.parentId;
            const visited = new Set<string>();

            while (currentParentId && !visited.has(currentParentId)) {
                visited.add(currentParentId);
                includedGroupIds.add(currentParentId);
                currentParentId = groups.find(
                    group => group.id === currentParentId,
                )?.parentId;
            }
        }

        return groups.filter(group => includedGroupIds.has(group.id));
    }, [activeRoleGroupIds, childrenByParentId, groups, role]);

    const groupById = useMemo(
        () => new Map(groups.map(group => [group.id, group])),
        [groups],
    );

    const hierarchyLevels = useMemo(() => {
        if (!hierarchy.length) {
            return [];
        }

        const byId = new Map(
            hierarchy.map(level => [level.id, level] as const),
        );
        const depthById = new Map<string, number>();

        const getDepth = (level: OrganizationHierarchyLevel): number => {
            if (depthById.has(level.id)) {
                return depthById.get(level.id) ?? 0;
            }

            if (!level.parentId) {
                depthById.set(level.id, 0);
                return 0;
            }

            const parent = byId.get(level.parentId);
            const depth = parent ? getDepth(parent) + 1 : 0;
            depthById.set(level.id, depth);
            return depth;
        };

        return [...hierarchy].sort((a, b) => getDepth(a) - getDepth(b));
    }, [hierarchy]);

    const filterHierarchyLevels = useMemo(() => {
        if (!canUseFilters) {
            return [];
        }

        const levelsWithoutRoom =
            hierarchyLevels.length > 2
                ? hierarchyLevels.slice(0, hierarchyLevels.length - 1)
                : hierarchyLevels;

        if (isOrganizationAdmin) {
            return levelsWithoutRoom;
        }

        return levelsWithoutRoom.length > 1
            ? levelsWithoutRoom.slice(1)
            : levelsWithoutRoom;
    }, [canUseFilters, hierarchyLevels, isOrganizationAdmin]);

    const isDescendantOf = useCallback(
        (group: OrganizationGroup, ancestorIds: Set<string>) => {
            let currentParentId = group.parentId;
            const visited = new Set<string>();

            while (currentParentId && !visited.has(currentParentId)) {
                if (ancestorIds.has(currentParentId)) {
                    return true;
                }

                visited.add(currentParentId);
                currentParentId = groupById.get(currentParentId)?.parentId;
            }

            return false;
        },
        [groupById],
    );

    const filterLevels = useMemo<OrganizationGroupFilterLevel[]>(() => {
        return filterHierarchyLevels
            .map((level, levelIndex) => {
                const groupsForLevel = filterableGroups.filter(
                    group => group.levelId === level.id,
                );
                const selectedAncestorIds = filterHierarchyLevels
                    .slice(0, levelIndex)
                    .flatMap(
                        ancestorLevel =>
                            selectedGroupIdsByLevel[ancestorLevel.id] ?? [],
                    );
                const selectedAncestorSet = new Set(selectedAncestorIds);
                const availableGroups = selectedAncestorIds.length
                    ? groupsForLevel.filter(group =>
                          isDescendantOf(group, selectedAncestorSet),
                      )
                    : groupsForLevel;

                return {
                    ...level,
                    groups: availableGroups,
                    options: availableGroups.map(group => ({
                        id: group.id,
                        label: group.name || group.id,
                    })),
                    selectedIds: selectedGroupIdsByLevel[level.id] ?? [],
                };
            })
            .filter(level => level.options.length > 0);
    }, [
        filterableGroups,
        filterHierarchyLevels,
        isDescendantOf,
        selectedGroupIdsByLevel,
    ]);

    useEffect(() => {
        const availableIdsByLevelId = new Map(
            filterLevels.map(
                level =>
                    [
                        level.id,
                        new Set(level.groups.map(group => group.id)),
                    ] as const,
            ),
        );
        const nextSelectedGroupIdsByLevel: Record<string, string[]> = {};
        let hasChanges = false;

        Object.entries(selectedGroupIdsByLevel).forEach(
            ([levelId, groupIds]) => {
                const availableIds = availableIdsByLevelId.get(levelId);
                const nextGroupIds = availableIds
                    ? groupIds.filter(groupId => availableIds.has(groupId))
                    : [];

                if (nextGroupIds.length) {
                    nextSelectedGroupIdsByLevel[levelId] = nextGroupIds;
                }

                if (nextGroupIds.length !== groupIds.length) {
                    hasChanges = true;
                }
            },
        );

        if (hasChanges) {
            setSelectedGroupIdsByLevel(nextSelectedGroupIdsByLevel);
        }
    }, [filterLevels, selectedGroupIdsByLevel]);

    const selectedFilterGroupIds = useMemo(() => {
        for (const level of [...filterHierarchyLevels].reverse()) {
            const selectedIds = selectedGroupIdsByLevel[level.id] ?? [];
            if (selectedIds.length) {
                return selectedIds;
            }
        }

        return [];
    }, [filterHierarchyLevels, selectedGroupIdsByLevel]);

    const selectedFilterCount = useMemo(
        () =>
            filterHierarchyLevels.reduce(
                (count, level) =>
                    count + (selectedGroupIdsByLevel[level.id]?.length ?? 0),
                0,
            ),
        [filterHierarchyLevels, selectedGroupIdsByLevel],
    );

    const setSelectedIdsForLevel = useCallback(
        (levelId: string, nextIds: string[]) => {
            setSelectedGroupIdsByLevel(current => ({
                ...current,
                [levelId]: nextIds,
            }));
        },
        [],
    );

    return {
        filterLevels,
        selectedFilterCount,
        selectedFilterGroupIds,
        setSelectedIdsForLevel,
    };
};
